import { useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import { useGroup } from '../contexts/GroupContext';
import { useAuth } from '../contexts/AuthContext';

// WHAT YOU MAY DO HERE. In your own space, everything. In a group there are two
// roles: an admin may do everything (and sits on the group's Full Access realm,
// which is said, never what decides); a member does what the realm an admin gave
// them allows, and nothing else: reading, writing, running plugins, answering
// proposals (GET /groups/<gid>/my-access, read once per group and kept). Every
// membership has a realm; "No access" grants nothing. A member may edit or trash
// a node they wrote themselves while they can still see it: the server allows
// it, and a group node row says so itself (`can: {edit, delete}`).
//
//   { role, roleWord, isAdmin, manage, realm, realmName, runPlugins, scoped, noAccess,
//     can(verb)            — read | create | edit | delete | propose, granted anywhere here
//     canOn(verb, node)    — on this node: the row's own `can` when it has one, else the
//                            verb with the author's exception (edit, delete)
//     wrote(node)          — did the viewer write it
//     hint                 — why a write is off ("Ask an admin of Book Club for write access")
//     pluginHint           — why a run is off ("Ask an admin of Book Club to let you run its plugins’ tools")
//     noAccessLine         — "You have no access in Book Club yet. Ask an admin."
//     onlyAdmin(what)      — why an admin's act is off ("Only an admin of Book Club can …") }

const NODE_VERBS = ['read', 'create', 'edit', 'delete', 'propose'];
// where every admin sits, before my-access has named the realm
const FULL_ACCESS = { id: null, name: 'Full Access', kind: 'full_access' };
const ALL = Object.fromEntries(NODE_VERBS.map((v) => [v, true]));

// gid:role → { value, promise }. The roster's role is in the key, so a promotion
// the roster has heard of reads my-access again.
const cache = new Map();
const keyOf = (gid, rosterRole) => `${gid}:${rosterRole || ''}`;

/** Forget every group's access (tests; a sign-out). */
export function resetSpaceAccess() { cache.clear(); }

/** The answer, once read, without asking: null until then. */
export function cachedAccess(gid, rosterRole) {
    return gid ? cache.get(keyOf(gid, rosterRole))?.value ?? null : null;
}

/** The role my-access answered for a group, whatever the roster said: null until read. */
export function knownRole(gid) {
    if (!gid) return null;
    for (const [k, e] of cache) if (k.startsWith(`${gid}:`) && e.value) return roleOf(e.value, null);
    return null;
}

function readAccess(gid, rosterRole) {
    const key = keyOf(gid, rosterRole);
    const hit = cache.get(key);
    if (hit) return hit.promise;
    const entry = { value: null, promise: null };
    entry.promise = Promise.resolve()
        .then(() => api.request(`/groups/${encodeURIComponent(gid)}/my-access`))
        .then((r) => (r && typeof r === 'object' && (r.role || r.nodes) ? r : null))
        .catch(() => null)
        .then((v) => {
            entry.value = v;
            // a failed read is asked again next time the page asks, not kept
            if (!v) cache.delete(key);
            return v;
        });
    cache.set(key, entry);
    return entry.promise;
}

/** The role in a group, from my-access, else the roster ('admin' | 'member' | null). */
export function roleOf(raw, rosterRole) {
    const r = raw?.role || (raw?.is_admin ? 'admin' : null) || rosterRole || null;
    return r === 'admin' || r === 'member' ? r : null;
}

/** Who wrote a node, as a person: { id, name } or null (an app's write, or unknown). */
export function authorOf(node) {
    const u = node?.created_by_user;
    const id = u?.id || node?.created_by_user_id || null;
    const name = u?.display_name || u?.username || node?.created_by_display_name || null;
    return id || name ? { id: id ? String(id) : null, name } : null;
}

/** Did this viewer write it. */
export const wroteIt = (node, userId) => {
    const a = authorOf(node);
    return !!userId && !!a?.id && a.id === String(userId);
};

/**
 * "Written by", said plainly: the app that wrote it, else the person ("You" for
 * the viewer), else in your own space "You", in a group null (nobody named).
 */
export function writerOf(node, userId, inGroup, group = null) {
    // a token is never an app: it is named as what it is (created_by_kind)
    if (node?.created_by_app_name) return node.created_by_kind === 'token' ? `Token: ${node.created_by_app_name}` : node.created_by_app_name;
    const a = authorOf(node);
    if (a?.id && userId && a.id === String(userId)) return 'You';
    // the group's own account (what it made itself, its profile): the group, by name
    if (inGroup && a?.id && group?.group_user_id && a.id === String(group.group_user_id)) return group.name || 'The group';
    if (a?.name) return a.name;
    return inGroup ? null : 'You';
}

/**
 * The space's own words, for sentences about its things: in your own space
 * "your" and "you"; in a group the group by name ("Book Club’s", "Book Club"),
 * never "your" for what is the group's.
 */
export function spaceWords(groupId, groupName) {
    if (!groupId) return { inGroup: false, your: 'your', Your: 'Your', you: 'you', You: 'You', youHavent: 'You haven’t', youHave: 'you have' };
    const n = groupName || 'This group';
    return { inGroup: true, your: `${n}’s`, Your: `${n}’s`, you: n, You: n, youHavent: `${n} hasn’t`, youHave: `${n} has` };
}

/** Everything the screens ask, for one space. Pure, so it is tested as is. */
export function spaceAccess({ groupId = null, groupName = null, raw = null, rosterRole = null, userId = null } = {}) {
    const name = groupName || 'this group';
    const wrote = (node) => wroteIt(node, userId);
    const onlyAdmin = (what = 'do this') => `Only an admin of ${name} can ${what}`;
    if (!groupId) {
        return {
            inGroup: false, role: null, roleWord: null, isAdmin: true, manage: true, realm: null, realmName: null,
            runPlugins: true, scoped: false, noAccess: false,
            nodes: ALL, can: () => true, canOn: () => true, wrote, hint: undefined, pluginHint: undefined,
            noAccessLine: undefined, onlyAdmin: () => undefined, known: true,
        };
    }
    const role = roleOf(raw, rosterRole);
    const isAdmin = role === 'admin';
    const given = raw?.nodes && typeof raw.nodes === 'object' ? raw.nodes : {};
    // until my-access answers, a member is taken to read (no flash of "no access")
    // and to write nothing
    const nodes = isAdmin ? ALL : Object.fromEntries(NODE_VERBS.map((v) => [v, v === 'read' ? (raw ? !!given.read && raw.no_access !== true : true) : !!given[v]]));
    const can = (verb) => !!nodes[verb];
    const noAccess = !isAdmin && !!raw && (raw.no_access === true || !nodes.read);
    // the node's own answer, scoped exactly by the backend (realm scope OR the
    // author's exception); the verb and the author's exception when it has none
    const canOn = (verb, node) => {
        if (isAdmin) return true;
        const own = node?.can;
        if ((verb === 'edit' || verb === 'delete') && own && typeof own[verb] === 'boolean') return own[verb];
        return can(verb) || ((verb === 'edit' || verb === 'delete') && wrote(node));
    };
    // every admin is on Full Access; the realm is named, the role decides
    const realm = isAdmin ? (raw?.realm || FULL_ACCESS) : raw?.realm || null;
    return {
        inGroup: true,
        role,
        roleWord: role === 'admin' ? 'Admin' : role === 'member' ? 'Member' : null,
        isAdmin,
        manage: isAdmin || raw?.manage === true,
        realm,
        realmName: realm?.name || null,
        // plugins.execute in their realm: run now, trigger, switch off
        runPlugins: isAdmin || (role === 'member' && raw?.run_plugins === true),
        scoped: !isAdmin && raw?.scoped === true,
        noAccess,
        nodes,
        can,
        canOn,
        wrote,
        hint: isAdmin ? undefined : `Ask an admin of ${name} for write access`,
        pluginHint: isAdmin ? undefined : `Ask an admin of ${name} to let you run its plugins’ tools`,
        noAccessLine: noAccess ? noAccessLine(groupName) : undefined,
        onlyAdmin: (what) => (isAdmin ? undefined : onlyAdmin(what)),
        known: !!raw,
    };
}

/** What a member with No access is told, on every page of the group's data. */
export const noAccessLine = (groupName) => `You have no access in ${groupName || 'this group'} yet. Ask an admin.`;

// my-access for the group you are in, read once and kept: the raw answer (null
// until it lands, or when it cannot be read) and the roster's role beside it.
function useRawAccess() {
    const { activeGroupId, activeGroup } = useGroup() || {};
    const gid = activeGroupId || null;
    const rosterRole = activeGroup?.my_role || activeGroup?.role || null;
    const [, setTick] = useState(0);
    useEffect(() => {
        if (!gid) return undefined;
        let live = true;
        if (!cachedAccess(gid, rosterRole)) readAccess(gid, rosterRole).then(() => { if (live) setTick((t) => t + 1); });
        return () => { live = false; };
    }, [gid, rosterRole]);
    return { gid, group: activeGroup || null, rosterRole, raw: cachedAccess(gid, rosterRole) };
}

/** Which space, which role: { group, role, roleWord, realmName } (no role in your own space). */
export function useSpaceRole() {
    const { gid, group, rosterRole, raw } = useRawAccess();
    const role = gid ? roleOf(raw, rosterRole) : null;
    // the realm is said after the role: a member's, or an admin's Full Access
    const realmName = role === 'member' ? raw?.realm?.name || null
        : role === 'admin' ? raw?.realm?.name || FULL_ACCESS.name : null;
    return { groupId: gid, group, role, roleWord: role === 'admin' ? 'Admin' : role === 'member' ? 'Member' : null, realmName };
}

// The viewer, when there is one (a page drawn outside the sign-in has none: its
// node verbs then lose only the author's exception). useAuth reads its context
// before it throws, so the hooks keep their order either way.
export function useViewer() {
    try { return useAuth()?.user || null; } catch { return null; }
}

/** What you may do in the space you are in. Reads my-access once per group. */
export function useSpaceAccess() {
    const { gid, group, rosterRole, raw } = useRawAccess();
    const user = useViewer();
    const userId = user?.id || user?.user_id || null;
    return useMemo(
        () => spaceAccess({ groupId: gid, groupName: group?.name, raw, rosterRole, userId }),
        [gid, group?.name, raw, rosterRole, userId],
    );
}
